import PropTypes from 'prop-types';
import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

// project imports
import IconButton from 'components/@extended/IconButton';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export default function NavItem({ item, level, isParents = false, setSelectedID, subItem = false }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const theme = useTheme();

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item.url, end: item.url === '/' ? true : false }, pathname);

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);
    if (isParents && setSelectedID) setSelectedID(item.id);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem',
        ...(isParents && { fontSize: 20, stroke: '1.5' })
      }}
    />
  ) : null;

  // Map item IDs to tour-friendly IDs
  const tourIdMap = {
    'dashboard': 'dashboard-nav-item',
    'rmb-customer-reviews': 'customer-reviews-nav-item',
    'rmb-customer-feedback': 'customer-feedback-nav-item',
    'rmb-widgets': 'widgets-nav-item',
    'rmb-qr-codes': 'qr-codes-nav-item',
    'cliniko-data': 'cliniko-data-nav-item',
    'bookings': 'bookings-nav-item',
    'automation-workflows': 'automation-nav-item',
    'settings': 'settings-nav-item'
  };

  const tourId = tourIdMap[item.id];

   
return (
    <Box sx={{ position: 'relative' }}>
      <ListItemButton
        id={tourId}
        component={Link}
        to={item.url}
        selected={isSelected}
        onClick={itemHandler}
        sx={{
          position: 'relative',
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,

          /* ================= SUB ITEM DOT + LINE ================= */
          ...(subItem && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: drawerOpen ? 12 : 6,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: isSelected ? 'primary.main' : '#BDB4D8',
              zIndex: 2
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: drawerOpen ? 14 : 8,
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: '#E0E0E0',
              zIndex: 1
            }
          }),

          /* ================= PARENT ITEM STYLES ================= */
          ...(!subItem && {
            '&:hover': {
              bgcolor: theme.palette.primary.lighter,
              '& .MuiTypography-root': { color: 'primary.main' },
              '& .MuiListItemIcon-root': { color: 'primary.main' }
            },
            '&.Mui-selected': {
              bgcolor: theme.palette.primary.light,
              '& .MuiTypography-root': { color: 'primary.main' },
              '& .MuiListItemIcon-root': { color: 'primary.main' },
              '&:hover': { bgcolor: theme.palette.primary.light }
            }
          }),

          /* ================= SUB ITEM STYLES ================= */
          ...(subItem && {
            '&:not(.Mui-selected):hover': {
              bgcolor: theme.palette.primary.lighter,
              '& .MuiTypography-root': { color: 'primary.main' },
              '& .MuiListItemIcon-root': { color: 'primary.main' }
            },
            '&.Mui-selected': {
              bgcolor: theme.palette.primary.light,
              '& .MuiTypography-root': { color: 'primary.main' },
              '& .MuiListItemIcon-root': { color: 'primary.main' },
              '&:hover': { bgcolor: theme.palette.primary.light }
            }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              width: 28,
              paddingLeft:drawerOpen?0:1,
              color: isSelected ? 'primary.main' : theme.palette.text.primary
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}

        {(drawerOpen || level !== 1) && (
          <ListItemText
            primary={
              <Typography variant="h6" sx={{ color: isSelected ? 'primary.main' : theme.palette.text.primary }}>
                {item.title}
              </Typography>
            }
          />
        )}

        {(drawerOpen || level !== 1) && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
      </ListItemButton>

      {(drawerOpen || level !== 1) &&
        item?.actions?.map((action, index) => {
          const ActionIcon = action.icon;
           
return (
            <IconButton
              key={index}
              component={action.type === 'link' ? Link : 'button'}
              to={action.url}
              onClick={(e) => {
                e.stopPropagation();
                action?.function?.();
              }}
              sx={{
                position: 'absolute',
                top: 12,
                right: 20,
                width: 20,
                height: 20,
                color: 'secondary.dark'
              }}
            >
              <ActionIcon style={{ fontSize: '0.625rem' }} />
            </IconButton>
          );
        })}
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func]),
  subItem: PropTypes.bool
};
